import { type LandingSectionDefinition, type LandingSectionField } from "../../fields";
import { type postPurchaseSectionTypes } from "./types";

const GROUP = "Upsell i downsell";

const OFFER_TOKENS_HELP =
  "Tokeny: {{cenaOferty}} = cena produktu tego kroku, {{cenaPoprzedniegoKroku}} = cena z poprzedniego kroku; po | wartość, gdy brak danych, np. {{cenaOferty|127}}.";

const spaceOptions = [
  { label: "Brak", value: "" },
  { label: "2 (8 px)", value: "2" },
  { label: "4 (16 px)", value: "4" },
  { label: "6 (24 px)", value: "6" },
  { label: "8 (32 px)", value: "8" },
  { label: "12 (48 px)", value: "12" },
  { label: "Rosnący: 16 / 32 / 48 px", value: "steps" },
];

/** Where the section sits on the page: width, side padding, spacing. Same keys in every `pp` section. */
const frameFields: LandingSectionField[] = [
  {
    key: "frame",
    label: "Kontener",
    type: "select",
    options: [
      { label: "Nagłówek strony (upsell)", value: "hero" },
      { label: "Nagłówek strony (szeroki margines)", value: "heroWide" },
      { label: "Wąska kolumna (768 px)", value: "narrow" },
      { label: "Szeroka kolumna (1152 px)", value: "wide" },
      { label: "Kolumna downsellu (768 px)", value: "column" },
      { label: "Kolumna downsellu (896 px)", value: "columnWide" },
    ],
  },
  {
    key: "paddingY",
    label: "Odstęp w pionie (padding)",
    type: "select",
    options: [
      { label: "Brak", value: "" },
      { label: "Sekcja: góra i dół", value: "section" },
      { label: "Sekcja: tylko góra", value: "sectionTop" },
      { label: "Sekcja: tylko dół", value: "sectionBottom" },
      { label: "Początek strony (downsell)", value: "pageTop" },
      { label: "Koniec strony (downsell)", value: "pageBottom" },
    ],
  },
  { key: "spaceBefore", label: "Odstęp przed sekcją", type: "select", options: spaceOptions },
  { key: "spaceAfter", label: "Odstęp po sekcji", type: "select", options: spaceOptions },
];

const paragraphStyleFields: LandingSectionField[] = [
  {
    key: "size",
    label: "Rozmiar tekstu",
    type: "select",
    options: [
      { label: "Jak w otoczeniu", value: "" },
      { label: "Mały (14 px)", value: "sm" },
      { label: "Mały, od tabletu 16 px", value: "smMd" },
      { label: "Zwykły (16 px)", value: "base" },
      { label: "Zwykły, od tabletu 18 px", value: "baseLg" },
      { label: "Duży (18 / 20 px)", value: "lg" },
    ],
  },
  { key: "relaxed", label: "Większa interlinia", type: "boolean" },
  { key: "bold", label: "Pogrubiony", type: "boolean" },
  {
    key: "gap",
    label: "Odstęp między akapitami",
    type: "select",
    options: [
      { label: "16 px", value: "4" },
      { label: "8 px", value: "2" },
      { label: "Pusta linia", value: "br" },
    ],
  },
];

const ctaStyleOptions = [
  { label: "Prostokątny, wyśrodkowany", value: "rounded" },
  { label: "Prostokątny, w tekście", value: "inline" },
  { label: "Zaokrąglony (pill)", value: "pill" },
];

export const postPurchaseSectionDefinitions: Record<
  (typeof postPurchaseSectionTypes)[number],
  LandingSectionDefinition
> = {
  ppHeading: {
    label: "Nagłówek oferty",
    group: GROUP,
    fields: [
      {
        key: "title",
        label: "Tytuł",
        type: "richText",
        help: "<em> = różowy fragment.",
      },
      { key: "subtitle", label: "Podtytuł", type: "richText" },
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "Upsell (36 / 60 px)", value: "upsell" },
          { label: "Ulepszenie zamówienia (30 / 48 / 60 px)", value: "complete" },
          { label: "Downsell (30 / 36 / 60 px)", value: "downsell" },
        ],
      },
      { key: "bannerTitle", label: "Różowy baner: tytuł", type: "richText" },
      { key: "bannerNote", label: "Różowy baner: dopisek", type: "richText", help: "<s> = przekreślona cena." },
      ...frameFields,
    ],
    defaultProps: {
      title: "<em>Gratulacje! </em>Twoje zamówienie zostało przyjęte!",
      style: "upsell",
      frame: "hero",
    },
  },
  ppProgress: {
    label: "Pasek postępu",
    group: GROUP,
    fields: [
      { key: "percent", label: "Procent", type: "number" },
      { key: "labelAbove", label: "Tekst nad paskiem", type: "text" },
      { key: "labelBelow", label: "Tekst pod paskiem", type: "text" },
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "Upsell", value: "upsell" },
          { label: "Ulepszenie zamówienia", value: "complete" },
          { label: "Downsell", value: "downsell" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      percent: 90,
      labelAbove: "✅ Prawie gotowe! 90% wykonane - ostatni krok do pełnego planu ⏳",
      style: "upsell",
      frame: "narrow",
    },
  },
  ppText: {
    label: "Tekst",
    group: GROUP,
    fields: [
      { key: "paragraphs", label: "Akapity", type: "stringList", help: `Jeden akapit w linii. ${OFFER_TOKENS_HELP}` },
      ...paragraphStyleFields,
      ...frameFields,
    ],
    defaultProps: {
      paragraphs: ["✔ Oferta jednorazowa - niedostępna później"],
      size: "sm",
      gap: "2",
      frame: "narrow",
    },
  },
  ppBox: {
    label: "Ramka z treścią",
    group: GROUP,
    fields: [
      {
        key: "boxStyle",
        label: "Tło ramki",
        type: "select",
        options: [
          { label: "Jasnoróżowe", value: "pink" },
          { label: "Łososiowe, duży padding", value: "rose" },
          { label: "Łososiowe, mały padding", value: "blush" },
        ],
      },
      {
        key: "layout",
        label: "Układ",
        type: "select",
        options: [
          { label: "Kolumna, odstęp 24 px", value: "stack" },
          { label: "Kolumna wyśrodkowana, odstęp 24 px", value: "centered" },
          { label: "Wyśrodkowany tekst, odstęp 24 px", value: "spaced" },
          { label: "Odstęp 16 px", value: "tight" },
          { label: "Bez odstępów", value: "plain" },
        ],
      },
      {
        key: "items",
        label: "Elementy",
        type: "list",
        itemLabel: "Element",
        fields: [
          {
            key: "kind",
            label: "Rodzaj",
            type: "select",
            options: [
              { label: "Tekst", value: "text" },
              { label: "Cena (nagłówek)", value: "price" },
              { label: "Obrazek", value: "image" },
              { label: "Przycisk", value: "cta" },
              { label: "Zdjęcie z analizą posiłku AI", value: "mealAnalysis" },
            ],
          },
          { key: "paragraphs", label: "Tekst: akapity", type: "stringList", help: OFFER_TOKENS_HELP },
          ...paragraphStyleFields,
          { key: "narrow", label: "Tekst: węższy (672 px)", type: "boolean" },
          {
            key: "price",
            label: "Cena",
            type: "richText",
            help: `<s> = stara cena, <em> = nowa cena. ${OFFER_TOKENS_HELP}`,
          },
          { key: "image", label: "Obrazek", type: "image" },
          { key: "alt", label: "Opis obrazka", type: "text" },
          {
            key: "imageSize",
            label: "Szerokość obrazka",
            type: "select",
            options: [
              { label: "Pełna (768 px)", value: "wide" },
              { label: "Średnia (512 px)", value: "medium" },
            ],
          },
          { key: "label", label: "Przycisk: tekst", type: "text" },
          { key: "href", label: "Przycisk: link", type: "url", help: "Domyślnie #form (formularz zakupu)." },
          { key: "ctaStyle", label: "Przycisk: wygląd", type: "select", options: ctaStyleOptions },
          { key: "badge", label: "Analiza AI: etykieta", type: "text" },
          { key: "kcal", label: "Analiza AI: kalorie", type: "text" },
          {
            key: "macros",
            label: "Analiza AI: makro (3 kółka)",
            type: "list",
            itemLabel: "Makro",
            fields: [
              { key: "value", label: "Wartość", type: "text" },
              { key: "label", label: "Skrót", type: "text" },
            ],
          },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      boxStyle: "pink",
      layout: "stack",
      items: [
        {
          kind: "text",
          paragraphs: ["Dodaj do swojego planu <strong>100+ fit przepisów premium</strong>."],
          size: "base",
          relaxed: true,
        },
        { kind: "cta", label: "Kupuję!", href: "#form", ctaStyle: "rounded" },
      ],
      frame: "narrow",
    },
  },
  ppOfferCard: {
    label: "Karta oferty z listą",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "text" },
      { key: "description", label: "Opis", type: "richText" },
      { key: "listTitle", label: "Nagłówek listy", type: "text" },
      {
        key: "items",
        label: "Lista",
        type: "list",
        itemLabel: "Punkt",
        fields: [
          { key: "icon", label: "Emoji", type: "text" },
          { key: "text", label: "Tekst", type: "text" },
          { key: "muted", label: "Wyszarzony (czego nie ma)", type: "boolean" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      title: "🩷 Prowadzenie Trenera - Pełne Wsparcie (6 tygodni)",
      description: "Przez całe wyzwanie masz indywidualne wsparcie trenera.",
      listTitle: "W ramach prowadzenia otrzymujesz:",
      items: [{ icon: "💬", text: "Stały kontakt tekstowy przez aplikację" }],
      frame: "narrow",
    },
  },
  ppCardGrid: {
    label: "Siatka kafelków z obrazkami",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "text" },
      {
        key: "items",
        label: "Kafelki",
        type: "list",
        itemLabel: "Kafelek",
        fields: [
          { key: "image", label: "Obrazek", type: "image" },
          { key: "alt", label: "Opis obrazka", type: "text" },
          { key: "text", label: "Tekst", type: "richText" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      title: "VIP Oferta:",
      items: [
        {
          image: "/assets/bikini/tablet-screen-mockup-1.png",
          alt: "Tablet mockup",
          text: "🍫 <strong>20 Nielegalnych fit słodyczy</strong> - lody, ciasta, batony",
        },
      ],
      frame: "wide",
      paddingY: "section",
    },
  },
  ppSplitChecklist: {
    label: "Zdjęcie i lista zalet",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "text" },
      { key: "items", label: "Zalety", type: "stringList", help: "Jedna w linii." },
      { key: "image", label: "Zdjęcie", type: "image" },
      { key: "alt", label: "Opis zdjęcia", type: "text" },
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "Upsell (zdjęcie obok)", value: "upsell" },
          { label: "Downsell (zdjęcie przy krawędzi)", value: "downsell" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      title: "🧠 Dlaczego warto?",
      items: ["Unikasz nudy w kuchni = lepsza motywacja i mniejsze ryzyko rezygnacji"],
      image: "https://api.builder.io/api/v1/image/assets/TEMP/fbd0462ca266a0404fa5227d4f66cb3aa840debb?width=514",
      alt: "Happy woman cooking",
      style: "upsell",
      frame: "wide",
      paddingY: "section",
    },
  },
  ppChecklist: {
    label: "Lista zalet z tytułem",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "richText" },
      { key: "items", label: "Zalety", type: "stringList", help: "Jedna w linii." },
      ...frameFields,
    ],
    defaultProps: {
      title: "Dlaczego to pokochasz?",
      items: ["Bo nie będziesz już zgadywać, czy możesz coś zjeść."],
      frame: "narrow",
      spaceAfter: "steps",
    },
  },
  ppComparison: {
    label: "Porównanie dwóch opcji",
    group: GROUP,
    fields: [
      { key: "leftTitle", label: "Lewa kolumna: tytuł", type: "text", help: OFFER_TOKENS_HELP },
      { key: "leftItems", label: "Lewa kolumna: punkty", type: "stringList" },
      { key: "rightTitle", label: "Prawa kolumna (wyróżniona): tytuł", type: "text", help: OFFER_TOKENS_HELP },
      { key: "rightItems", label: "Prawa kolumna: punkty", type: "stringList" },
      { key: "footnote", label: "Tekst pod porównaniem", type: "richText" },
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "❌ / ✅, większe tytuły", value: "crossCheck" },
          { label: "Kropki, mniejsze tytuły", value: "bullets" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      leftTitle: "Bez prowadzenia trenera:",
      leftItems: ["wszystko robisz samodzielnie"],
      rightTitle: "Z prowadzeniem trenera:",
      rightItems: ["ktoś realnie patrzy na Twoje postępy"],
      style: "crossCheck",
      frame: "wide",
      paddingY: "section",
    },
  },
  ppStats: {
    label: "Statystyki",
    group: GROUP,
    fields: [
      { key: "intro", label: "Tekst nad liczbami", type: "text" },
      {
        key: "items",
        label: "Liczby",
        type: "list",
        itemLabel: "Liczba",
        fields: [
          { key: "value", label: "Wartość", type: "text" },
          { key: "label", label: "Opis", type: "richText" },
          {
            key: "color",
            label: "Kolor",
            type: "select",
            options: [
              { label: "Zielony", value: "green" },
              { label: "Czerwony", value: "red" },
            ],
          },
        ],
      },
      { key: "footer", label: "Tekst pod liczbami", type: "richText" },
      ...frameFields,
    ],
    defaultProps: {
      intro: "Według naszych wewnętrznych danych:",
      items: [
        { value: "83%", label: "kobiet kończy wyzwanie z sukcesem <strong>z trenerem</strong>", color: "green" },
        { value: "14%", label: "kobiet kończy wyzwanie z sukcesem <strong>bez trenera</strong>", color: "red" },
      ],
      frame: "wide",
      paddingY: "section",
    },
  },
  ppPriceBox: {
    label: "Ramka z ceną",
    group: GROUP,
    fields: [
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "Upsell (z uzasadnieniem)", value: "upsell" },
          { label: "Downsell (sama cena)", value: "downsell" },
        ],
      },
      { key: "title", label: "Tytuł (upsell)", type: "text" },
      { key: "standardLabel", label: "Nad ceną standardową (downsell)", type: "text" },
      { key: "standardPrice", label: "Cena standardowa", type: "richText", help: "<s> = przekreślenie." },
      { key: "standardNote", label: "Dopisek do ceny standardowej (upsell)", type: "text" },
      { key: "reasonTitle", label: "Pytanie (upsell)", type: "text" },
      { key: "reasonText", label: "Odpowiedź (upsell)", type: "richText" },
      { key: "priceLabel", label: "Nad ceną", type: "text" },
      {
        key: "price",
        label: "Cena",
        type: "richText",
        help: `<em> = nowa cena, <s> = stara cena, <small> = mniejszy tekst. ${OFFER_TOKENS_HELP}`,
      },
      { key: "note", label: "Dopisek pod ceną", type: "text" },
      ...frameFields,
    ],
    defaultProps: {
      style: "upsell",
      title: "🔥 Ile to normalnie kosztuje?",
      standardPrice: "Standardowa cena prowadzenia trenera: <strong><s>700 zł / 6 tygodni</s></strong>",
      priceLabel: "👉 Cena tylko teraz:",
      price: "{{cenaOferty|147}} zł <s>zamiast 700 zł</s>",
      note: "(oferta dostępna wyłącznie na tym etapie, tylko dla uczestniczek wyzwania)",
      frame: "wide",
      paddingY: "section",
    },
  },
  ppTestimonials: {
    label: "Opinie",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "richText", help: "<em> = różowy fragment." },
      {
        key: "style",
        label: "Wygląd",
        type: "select",
        options: [
          { label: "3 kolumny", value: "grid3" },
          { label: "2 kolumny", value: "grid2" },
          { label: "Jedna, wyśrodkowana", value: "single" },
        ],
      },
      {
        key: "items",
        label: "Opinie",
        type: "list",
        itemLabel: "Opinia",
        fields: [
          { key: "quote", label: "Cytat", type: "textarea" },
          { key: "author", label: "Autorka", type: "text" },
        ],
      },
      ...frameFields,
    ],
    defaultProps: {
      style: "grid3",
      items: [{ quote: '„Polecam!"', author: "- Kasia, 37 lat" }],
      frame: "wide",
      paddingY: "section",
    },
  },
  ppCountdownBox: {
    label: "Ramka z odliczaniem",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "text" },
      { key: "minutes", label: "Minuty odliczania", type: "number" },
      { key: "text", label: "Tekst", type: "richText" },
      {
        key: "priceLine",
        label: "Linia z ceną",
        type: "richText",
        help: "<s> = stara cena, <strong> = nowa cena (duża).",
      },
      ...frameFields,
    ],
    defaultProps: {
      title: "⏰ TYLKO TERAZ - Oferta kończy się za:",
      minutes: 15,
      text: "To ekskluzywny dodatek dostępny tylko tutaj, tylko teraz - jednorazowo.",
      priceLine: "Promocja tylko teraz <s>197 zł</s> → <strong>37 zł</strong>",
      frame: "narrow",
    },
  },
  ppDivider: {
    label: "Linia oddzielająca",
    group: GROUP,
    fields: [{ key: "count", label: "Liczba linii (1 lub 2)", type: "number" }, ...frameFields],
    defaultProps: { count: 1, frame: "narrow", spaceAfter: "steps" },
  },
  ppOfferForm: {
    label: "Formularz zakupu (upsell / downsell)",
    group: GROUP,
    fields: [
      {
        key: "separators",
        label: "Linie nad formularzem",
        type: "select",
        options: [
          { label: "Brak", value: "0" },
          { label: "Jedna", value: "1" },
          { label: "Dwie", value: "2" },
        ],
      },
      {
        key: "separatorGap",
        label: "Odstęp między liniami a formularzem",
        type: "select",
        options: [
          { label: "24 px", value: "6" },
          { label: "32 px", value: "8" },
        ],
      },
      { key: "centered", label: "Formularz wyśrodkowany", type: "boolean" },
      {
        key: "isHighTicket",
        label: "Produkt high ticket",
        type: "boolean",
        help: "Jak w ofercie prowadzenia trenera: inny przebieg zamówienia.",
      },
      {
        key: "productCheckoutName",
        label: "Nazwa produktu w zamówieniu",
        type: "text",
        help: "Pusta = nazwa produktu z funnelu.",
      },
      { key: "facebookPixel", label: "Piksel Facebooka produktu", type: "boolean" },
      ...frameFields,
    ],
    defaultProps: {
      separators: "2",
      separatorGap: "8",
      facebookPixel: true,
      frame: "narrow",
      paddingY: "sectionBottom",
      spaceBefore: "8",
    },
  },
};
